import { useCallback } from 'react';

import {
  Box,
  FormControl,
  Grid,
  IconButton,
  Input,
  InputLabel,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  type Control,
  type FieldErrors,
  type Path,
  type UseFormRegister,
  type UseFormSetValue,
  useFieldArray,
} from 'react-hook-form';
import { XCircle } from 'lucide-react';

import HelperTextContainer from '@components/HelperTextContainer';
import FormSelect from '@components/FormFields/FormSelect';
import FilesContainer from '@pages/common/Project/Forms/Components/FilesContainer';
import { useSampleFiles } from '@pages/common/Project/Forms/hooks';
import { SampleContainer } from '@pages/common/Project/Forms/StyledComponents';
import {
  type SampleSchema,
  type ProjectSchema,
  type SampleFileSchema,
} from '@/zod_schemas';
import SampleIDComponent from './SampleIDComponent';

interface ISampleFormProps {
  register: UseFormRegister<ProjectSchema>;
  remove: () => void;
  index: number;
  experimentIndex: number;
  errors: FieldErrors<ProjectSchema>;
  control: Control<ProjectSchema>;
  setValue: UseFormSetValue<ProjectSchema>;
}

const SampleForm = ({
  remove,
  register,
  index,
  experimentIndex,
  errors,
  setValue,
  control,
}: ISampleFormProps) => {
  const sampleErrors = errors.experiments?.[experimentIndex]?.samples?.[index];
  const samplePath: Path<ProjectSchema> = `experiments.${experimentIndex}.samples.${index}`;
  const { fields, replace } = useFieldArray({
    control,
    name: `${samplePath}.files`,
  });

  // useCallback is used to prevent infinite loop
  const replaceFiles = useCallback(
    (s: SampleFileSchema[]) => replace(s),
    [replace],
  );

  useSampleFiles({
    control,
    sampleIndex: index,
    experimentIndex,
    replace: replaceFiles,
    fields,
  });

  const loadSampleData = (sample: SampleSchema | string) => {
    if (typeof sample === 'object') {
      // Set the form values for sample_id, gender, sample_type, and files
      setValue(`${samplePath}.sample_id`, sample.sample_id || '');
      setValue(`${samplePath}.gender`, sample.gender || 'unknown');
      setValue(`${samplePath}.sample_type`, sample.sample_type || 'bam');
      setValue(`${samplePath}.files`, sample.files || []);
      setValue(`${samplePath}.id`, sample.id || '');
    }
  };

  const clearSampleData = () => {
    setValue(`${samplePath}.sample_id`, '');
    setValue(`${samplePath}.id`, '');
  };

  return (
    <SampleContainer>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="subtitle2">Sample {index + 1}</Typography>
        </Box>
        <Tooltip title="Remove sample">
          <IconButton aria-label="remove sample" size="small" onClick={remove}>
            <XCircle size={17} />
          </IconButton>
        </Tooltip>
      </Box>
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <SampleIDComponent
              index={index}
              experimentIndex={experimentIndex}
              errors={errors}
              control={control}
              loadSampleData={loadSampleData}
              clearSampleData={clearSampleData}
            />
          </FormControl>

          {/* <FormControl fullWidth size="small" variant="standard">
            <Controller
              name={`${samplePath}.sample_id`}
              control={control}
              render={({ field }) => (
                <Autocomplete
                  freeSolo
                  selectOnFocus
                  handleHomeEndKeys
                  options={listSampleID}
                  loading={isFetching}
                  inputValue={inputValue}
                  onInputChange={(_, newInputValue) => {
                    setInputValue(newInputValue);
                    debouncedSetSearchInput(newInputValue);
                  }}
                  onChange={(_event, newValue) => {
                    if (newValue && typeof newValue === 'object') {
                      field.onChange(newValue.sample_id || '');
                      loadSampleData(newValue);
                    }
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filterOptions(options, params);
                    const { inputValue: filterInputValue } = params;

                    // Filter to show only matching or partially matching items
                    const strictFiltered = filtered.filter((option) =>
                      option.sample_id
                        .toLowerCase()
                        .startsWith(filterInputValue.toLowerCase()),
                    );

                    // Check if exact match exists
                    const isExactMatch = options.some(
                      (option) =>
                        option.sample_id.toLowerCase() ===
                        filterInputValue.toLowerCase(),
                    );

                    // Only add "create new" if there's no exact match and user has typed something
                    if (filterInputValue !== '' && !isExactMatch) {
                      strictFiltered.push({
                        isNew: true,
                        inputValue: filterInputValue,
                        sample_id: filterInputValue,
                        gender: 'male',
                        sample_type: 'bam',
                        files: [
                          {
                            file_path: 'string',
                            file_type: 'bam',
                          },
                        ],
                      });
                    }

                    return strictFiltered;
                  }}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    if (option.inputValue) return option.inputValue;
                    return option.sample_id;
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.sample_id === value.sample_id
                  }
                  renderOption={(props, option) => (
                    <MenuItem {...props} key={option.sample_id}>
                      {option.isNew
                        ? `Add '${option.sample_id}'`
                        : option.sample_id}
                    </MenuItem>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Sample ID"
                      variant="standard"
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          const selectedOption = listSampleID.find(
                            (opt) => opt.sample_id === inputValue,
                          );

                          const selected = selectedOption ?? {
                            isNew: true,
                            sample_id: inputValue,
                            gender: 'male',
                            sample_type: 'bam',
                            files: [{ file_path: 'string', file_type: 'bam' }],
                          };

                          field.onChange(inputValue);
                          loadSampleData(selected);
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isFetching ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
            <HelperTextContainer>
              <span>{sampleErrors?.sample_id?.message}</span>
            </HelperTextContainer>
          </FormControl> */}

          <FormControl
            fullWidth
            size="small"
            variant="standard"
            sx={{ display: 'none' }}
          >
            <InputLabel htmlFor="sample-id">ID</InputLabel>
            <Input
              id="sample-id"
              type="text"
              aria-describedby="sample id field"
              size="medium"
              fullWidth
              {...register(`${samplePath}.id`)}
            />
            <HelperTextContainer>
              {sampleErrors?.id?.message}
            </HelperTextContainer>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="sample-file-type-label" htmlFor="sample-file-type">
              Type
            </InputLabel>
            <FormSelect
              selectProps={{
                labelId: 'sample-file-type-label',
                id: 'sample-file-type',
                label: 'Type',
                'aria-describedby': 'sample file type field',
              }}
              controllerProps={{
                control,
                name: `${samplePath}.sample_type`,
              }}
            >
              <MenuItem value="unpaired_fastq">Unpaired FASTQ</MenuItem>
              <MenuItem value="paired_fastq">Paired FASTQ</MenuItem>
              <MenuItem value="bam">BAM</MenuItem>
            </FormSelect>
            <HelperTextContainer>
              {sampleErrors?.sample_type?.message}
            </HelperTextContainer>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="sample-gender-label" htmlFor="sample-gender">
              Gender
            </InputLabel>
            <FormSelect
              controllerProps={{
                control,
                name: `${samplePath}.gender`,
              }}
              selectProps={{
                labelId: 'sample-gender-label',
                id: 'sample-gender',
                label: 'Gender',
                'aria-describedby': 'sample gender field',
              }}
            >
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="unknown">Unknown</MenuItem>
            </FormSelect>
            <HelperTextContainer>
              {sampleErrors?.gender && (
                <span>{sampleErrors.gender.message}</span>
              )}
            </HelperTextContainer>
          </FormControl>
        </Grid>
        {fields.length > 0 && (
          <Grid item xs={12}>
            <FilesContainer
              control={control}
              errors={errors}
              experimentIndex={experimentIndex}
              sampleIndex={index}
              fields={fields}
            />
          </Grid>
        )}
      </Grid>
    </SampleContainer>
  );
};

export default SampleForm;
